/**
 * See360 Image Preloader
 * Two-pass progressive loading: key frames first for instant interactivity,
 * then fills remaining frames in the background.
 */

export interface PreloaderOptions {
  cdnBaseUrl: string;
  cdnBasePath: string;
  imagePattern: string;
  frameCount: number;
  keyFrameCount?: number;           // How many key frames to load first (default: 6)
  containerWidth?: number;          // Container width in px — used to choose frame size
  onProgress?: (loaded: number, total: number) => void;
  onKeyFramesLoaded?: (images: (HTMLImageElement | null)[], keyIndices: number[]) => void;
  onComplete?: (images: HTMLImageElement[]) => void;
  onError?: (error: Error, frameIndex: number) => void;
}

export class Preloader {
  private images: (HTMLImageElement | null)[] = [];
  private loaded = 0;
  private options: PreloaderOptions;
  private useSmallWebp: boolean;

  constructor(options: PreloaderOptions) {
    this.options = options;
    // Use small WebP frames when container is compact and browser supports WebP
    const containerWidth = options.containerWidth || 1280;
    this.useSmallWebp = containerWidth < 800 && Preloader.supportsWebp();
  }

  /** Check WebP support (cached) */
  private static _webpSupported: boolean | null = null;
  private static supportsWebp(): boolean {
    if (Preloader._webpSupported !== null) return Preloader._webpSupported;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      Preloader._webpSupported = canvas.toDataURL('image/webp').startsWith('data:image/webp');
    } catch {
      Preloader._webpSupported = false;
    }
    return Preloader._webpSupported;
  }

  /**
   * Build the URL for a given frame index (0-based)
   */
  private buildUrl(index: number): string {
    const { cdnBaseUrl, cdnBasePath, imagePattern } = this.options;
    // Frames are 1-indexed and zero-padded to 3 digits (FFmpeg convention: %03d)
    const frameIndex = String(index + 1).padStart(3, '0');
    const baseUrl = `${cdnBaseUrl}/${cdnBasePath}${imagePattern.replace('{index}', frameIndex)}`;

    if (this.useSmallWebp) {
      // exterior_001.jpg → exterior_001_sm.webp
      return baseUrl.replace('.jpg', '_sm.webp');
    }
    return baseUrl;
  }

  /**
   * Load a single image by 0-based index.
   * Falls back to JPEG if WebP version doesn't exist (older vehicles).
   */
  private loadImage(index: number): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      const src = this.buildUrl(index);

      img.onload = () => {
        this.images[index] = img;
        this.loaded++;
        this.options.onProgress?.(this.loaded, this.options.frameCount);
        resolve(img);
      };

      img.onerror = () => {
        // If WebP failed, fall back to JPEG
        if (this.useSmallWebp && src.endsWith('.webp')) {
          const jpegSrc = src.replace('_sm.webp', '.jpg');
          const fallbackImg = new Image();
          fallbackImg.onload = () => {
            this.images[index] = fallbackImg;
            this.loaded++;
            this.options.onProgress?.(this.loaded, this.options.frameCount);
            // Disable WebP for remaining frames (this vehicle doesn't have them)
            this.useSmallWebp = false;
            resolve(fallbackImg);
          };
          fallbackImg.onerror = () => {
            const error = new Error(`Failed to load frame ${index}: ${jpegSrc}`);
            this.options.onError?.(error, index);
            reject(error);
          };
          fallbackImg.src = jpegSrc;
          return;
        }

        const error = new Error(`Failed to load frame ${index}: ${src}`);
        this.options.onError?.(error, index);
        reject(error);
      };

      img.src = src;
    });
  }

  /**
   * Calculate which indices are "key frames" — evenly spaced across the full rotation.
   * For 36 frames with 6 keys: indices [0, 6, 12, 18, 24, 30]
   */
  private getKeyFrameIndices(): number[] {
    const { frameCount, keyFrameCount = 6 } = this.options;
    const count = Math.min(keyFrameCount, frameCount);
    const step = Math.floor(frameCount / count);
    const indices: number[] = [];
    for (let i = 0; i < count; i++) {
      indices.push(i * step);
    }
    return indices;
  }

  /**
   * Progressive load: key frames first, then remaining frames.
   */
  async load(): Promise<HTMLImageElement[]> {
    const { frameCount, onKeyFramesLoaded, onComplete } = this.options;

    // Initialize sparse array
    this.images = new Array(frameCount).fill(null);
    this.loaded = 0;

    // ── Pass 1: Load key frames (instant interactivity) ──
    const keyIndices = this.getKeyFrameIndices();

    try {
      await Promise.all(keyIndices.map(i => this.loadImage(i)));
    } catch {
      // Continue even if some key frames fail
    }

    // Fire key frames callback — viewer becomes interactive NOW
    onKeyFramesLoaded?.(this.images, keyIndices);

    // ── Pass 2: Fill remaining frames in background ──
    const remainingIndices = Array.from({ length: frameCount }, (_, i) => i)
      .filter(i => !keyIndices.includes(i));

    // Load remaining frames in small batches to avoid overwhelming the browser
    const BATCH_SIZE = 6;
    for (let batch = 0; batch < remainingIndices.length; batch += BATCH_SIZE) {
      const batchIndices = remainingIndices.slice(batch, batch + BATCH_SIZE);
      try {
        await Promise.all(batchIndices.map(i => this.loadImage(i)));
      } catch {
        // Continue loading remaining frames even if some fail
      }
    }

    // All frames loaded — compact the array (filter out any nulls from failures)
    const allImages = this.images.filter((img): img is HTMLImageElement => img !== null);
    onComplete?.(allImages);
    return allImages;
  }

  /**
   * Get the current sparse image array (includes nulls for unloaded frames)
   */
  getSparseImages(): (HTMLImageElement | null)[] {
    return this.images;
  }

  getImages(): HTMLImageElement[] {
    return this.images.filter((img): img is HTMLImageElement => img !== null);
  }

  getProgress(): { loaded: number; total: number; percent: number } {
    return {
      loaded: this.loaded,
      total: this.options.frameCount,
      percent: this.options.frameCount > 0 ? Math.round((this.loaded / this.options.frameCount) * 100) : 0,
    };
  }
}
